import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

import isEmail from 'validator/lib/isEmail';

import App from '../../../assets/js/app';
import DetectController from '../../../assets/js/controllers/detect';
import ScrollController from '../../../assets/js/controllers/scroll';
import XHR from '../../../assets/js/utils/xhr';

import { getRootOffset, parseHydration } from '../../../assets/js/helpers';

gsap.registerPlugin(SplitText);

export default class HeroSignUp {

	static selector = 'hero-sign-up';

	isDesktop;
	isTablet;

	el = {};
	interests = {};

	activeInterest;
	data;
	timelineShow;

	validInputs = 0;
	totalInputs = 0;

	constructor(container) {
		this.el = {
			container,
			heading: container.querySelector(`.${this.constructor.selector}__heading span`),
			buttons: container.querySelectorAll(`.${this.constructor.selector}__container button`),
			buttonItems: container.querySelectorAll(`.${this.constructor.selector}__button-item`),
			buttonsWrapper: container.querySelector(`.${this.constructor.selector}__container`),
			label: container.querySelector(`.${this.constructor.selector}__label`),
			background: container.querySelector(`.${this.constructor.selector}__background`),

			form: container.querySelector(`.${this.constructor.selector} form`),
			formBack: container.querySelector(`.${this.constructor.selector}__form-back`),
			formScroll: container.querySelector(`.${this.constructor.selector}__form-scroll`),
			formContainer: container.querySelector(`.${this.constructor.selector}__form`),
			formGroups: container.querySelectorAll(`.${this.constructor.selector} .form__group`),
			formMessage: container.querySelector(`.${this.constructor.selector} .form__message`),
			formHeadings: container.querySelectorAll(`.${this.constructor.selector}__form-content > div > h4`),

			inputEmail: container.querySelector(`.${this.constructor.selector} input[type="email"]`),
			inputsText: container.querySelectorAll(`.${this.constructor.selector} input[type="text"]`),

			submit: container.querySelector(`.${this.constructor.selector} button[type="submit"]`)
		};

		this.isDesktop = DetectController.isDesktop;
		this.isTablet = DetectController.isTablet;

		this.data = parseHydration(container);

		this.interests = {
			creative: {
				id: 'a62a4bd4fd'
			},
			agency: {
				id: '43ee86267b'
			},
			brand: {
				id: '51e0dc9795'
			},
			nonprofit: {
				id: '5d05ffd2d8'
			}
		};

		this.totalInputs = this.el.formGroups.length;

		if (this.isDesktop) {
			this.splitHeadings = [];

			for (const heading of this.el.formHeadings) {
				const splitHeading1 = new SplitText(heading, { type: 'lines' });
				this.splitHeadings.push(new SplitText(splitHeading1.lines, { type: 'lines' }));
			}
		}

		this.checkInterest();
		this.addEventListeners();
	}

	setFormScrollHeight() {
		const { top } = getRootOffset(this.el.formScroll);
		this.el.formScroll.style.height = `${window.innerHeight - top}px`;
	}

	addEventListeners() {
		if (this.isDesktop) {
			App.on('preloadComplete', this.#onPreloadComplete);
			App.on('pageTransition', this.#onPageTransition);
		}

		for (const button of this.el.buttons) {
			button.addEventListener('click', this.#onButtonClick);
		}

		this.el.formBack.addEventListener('click', this.#onFormBackClick);
		this.el.form.addEventListener('submit', this.#onFormSubmit);
	}

	removeEventListeners() {
		if (this.isDesktop) {
			App.off('preloadComplete', this.#onPreloadComplete);
			App.off('pageTransition', this.#onPageTransition);
		}

		for (const button of this.el.buttons) {
			button.removeEventListener('click', this.#onButtonClick);
		}

		this.el.formBack.removeEventListener('click', this.#onFormBackClick);
		this.el.form.removeEventListener('submit', this.#onFormSubmit);
	}

	#onButtonClick = (e) => {
		const { currentTarget } = e;

		const event = new Event('signup:click');
		this.el.container.dispatchEvent(event);

		const interest = currentTarget.getAttribute('data-interest');
		this.activeInterest = this.interests[interest];
		this.el.label.innerHTML = interest;
		this.el.buttonsWrapper.classList.add('is-transition');

		if (this.isDesktop) {
			this.hide().then(() => {
				this.showForm(interest);
				this.setFormScrollHeight();
			});
		} else if (this.isTablet) {
			this.showMobile(interest, true);
		} else {
			this.showMobile(interest);
		}
	};

	showMobile(interest, setHeight = false) {
		gsap.to(this.el.background, {
			autoAlpha: 0,
			duration: 0.25
		});
		this.showForm(interest);
		if (setHeight) this.setFormScrollHeight();
		ScrollController.adjust();
	}

	#onFormBackClick = () => {
		const event = new Event('signup:back');
		this.el.container.dispatchEvent(event);

		const interest = Object.keys(this.interests).find(key => this.interests[key] === this.activeInterest);
		this.hideForm(interest);

		this.activeInterest = null;
		this.show();

		if (!this.isDesktop) {
			gsap.to(this.el.background, {
				autoAlpha: 1,
				duration: 0.25
			});
			ScrollController.adjust();
		}
	};

	#onPreloadComplete = () => {
		this.show();
	};

	#onPageTransition = () => {
		this.show();
	};

	showForm(type) {
		if (type !== 'creative') this.totalInputs -= 1;
		this.el.buttonsWrapper.classList.remove('is-transition');
		this.el.container.classList.add(`is-${type}`);
		this.el.buttonsWrapper.classList.add('is-inactive');
		this.el.formContainer.classList.add('is-active');
	}

	hideForm(type) {
		this.resetForm();
		setTimeout(() => this.el.container.classList.remove(`is-${type}`), 1000);
		this.el.buttonsWrapper.classList.remove('is-inactive');
		this.el.formContainer.classList.remove('is-active');
	}

	resetForm() {
		this.el.submit.innerHTML = '<span>Submit</span><span>↗</span>';
		this.el.submit.disabled = false;
		this.totalInputs = this.el.formGroups.length;
		this.validInputs = 0;

		for (const inputText of this.el.inputsText) {
			inputText.parentElement.classList.remove('is-invalid');
			inputText.value = '';
		}

		this.el.inputEmail.parentElement.classList.remove('is-invalid');
		this.el.inputEmail.value = '';
	}

	show() {
		this.timelineShow = gsap.timeline({
			onComplete: () => {
				this.timelineShow.kill();
				this.timelineShow = null;
			}
		});
		if (this.isDesktop) {
			this.timelineShow
				.fromTo(this.el.heading, {
					y: '100%'
				}, {
					ease: 'expo.out',
					duration: 1.5,
					y: '0%',
				}, 'start+=1')
				.fromTo(this.el.buttonItems, {
					autoAlpha: 0,
					rotationX: -100,
					y: this.el.buttonItems[0].offsetHeight * 0.2,
					transformOrigin: '50% 50% -150'
				}, {
					ease: 'power3.inOut',
					autoAlpha: 1,
					rotationX: 0,
					y: 0,
					duration: 2,
					transformOrigin: '50% 50% -150',
					stagger: {
						each: 0.15
					}
				}, 'start+=0.5');
		} else {
			this.timelineShow
				.fromTo([this.el.heading, ...this.el.buttonItems], {
					autoAlpha: 0
				}, {
					ease: 'expo.out',
					duration: 1,
					autoAlpha: 1
				}, 'start+=0.3');
		}
	}

	hide() {
		return new Promise((resolve) => {
			this.timelineHide = gsap.timeline({
				onComplete: () => {
					this.timelineHide.kill();
					this.timelineHide = null;
					resolve();
				}
			});
			this.timelineHide
				.fromTo(this.el.heading, {
					y: '0%',
				}, {
					ease: 'power3.inOut',
					duration: 1,
					y: '-100%',
				}, 'start')
				.fromTo(this.el.buttonItems, {
					autoAlpha: 1,
					rotationX: 0,
					y: 0
				}, {
					ease: 'power3.inOut',
					duration: 1,
					autoAlpha: 0,
					rotationX: 30,
					y: this.el.buttonItems[0].offsetHeight * 0.2 * -1,
					transformOrigin: '50% 50% -150',
					stagger: {
						each: 0.1
					}
				}, 'start');
		});
	}

	// Form stuff

	#onFormSubmit = (e) => {
		e.preventDefault();

		this.validate();

		if (this.validInputs === this.totalInputs) {
			this.submitForm();
		} else {
			this.el.formMessage.innerHTML = 'Fix the above errors and try again!';
		}
	}

	validate() {
		this.validInputs = 0;

		for (const inputText of this.el.inputsText) {
			if (inputText.value) {
				inputText.parentElement.classList.remove('is-invalid');
				this.validInputs++;
			} else {
				inputText.parentElement.classList.add('is-invalid');
			}
		}

		if (isEmail(this.el.inputEmail.value)) {
			this.el.inputEmail.parentElement.classList.remove('is-invalid');
			this.validInputs++;
		} else {
			this.el.inputEmail.parentElement.classList.add('is-invalid');
		}
	}

	checkInterest() {
		if (window.location.search) {
			const urlParams = new URLSearchParams(window.location.search);
			const interest = urlParams.get('interest');
			this.activeInterest = this.interests[interest];
			this.el.label.innerHTML = interest;

			if (this.isDesktop) {
				this.showForm(interest);
				this.setFormScrollHeight();
			} else if (this.isTablet) {
				this.showMobile(interest, true);
			} else {
				this.showMobile(interest);
			}
		}
	}

	submitForm() {
		const data = new FormData(this.el.form);

		data.append('interest', this.activeInterest.id);

		return new Promise((resolve) => {
			new XHR('json')
				.post('/php/mailchimp.php', data)
				.then(xhr => {
					this.resetForm();
					this.el.submit.disabled = true;
					this.el.formMessage.innerHTML = '';

					if (xhr.response.status === 'subscribed') {
						this.el.submit.innerHTML = 'Thanks for signing up!';
						resolve();
					} else {
						this.el.submit.innerHTML = 'You are subscribed already or something went wrong!';
						setTimeout(() => {
							this.resetForm();
						}, 5000);
					}
				});
		});
	}

	destroy() {
		this.removeEventListeners();
		this.activeInterest = null;
	}

}
